import { CurrencyPipe, DecimalPipe, LowerCasePipe, NgOptimizedImage } from '@angular/common';
import { ChangeDetectionStrategy, Component, computed, inject, OnInit, signal, WritableSignal } from '@angular/core';
import { FormBuilder, FormControl, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterLink } from '@angular/router';
import { BadgeComponent, ButtonComponent, InputComponent, TextareaComponent } from '@core/ui-kit/components';
import { environment } from '@environments/environment';
import { TranslocoPipe } from '@jsverse/transloco';
import { DialogCloseDirective, DialogRef } from '@ngneat/dialog';
import { PaymentData, Product } from '@shared/data-access/data-access-payment';
import { isDefined } from '@shared/utils/is-defined.utils';
import { AnimationOptions, LottieComponent } from 'ngx-lottie';

@Component({
  selector: 'app-buy-tokens-dialog',
  imports: [
    NgOptimizedImage,
    InputComponent,
    TextareaComponent,
    TranslocoPipe,
    BadgeComponent,
    ButtonComponent,
    CurrencyPipe,
    ReactiveFormsModule,
    DialogCloseDirective,
    RouterLink,
    LottieComponent,
    LowerCasePipe,
    DecimalPipe,
  ],
  templateUrl: './buy-tokens-dialog.component.html',
  styleUrl: './buy-tokens-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class BuyTokensDialogComponent implements OnInit {
  private readonly fb = inject(FormBuilder);
  private readonly ref = inject(DialogRef);

  buyTokensForm = this.fb.group({
    amount: [1, Validators.required],
    billingAddress: [
      { value: 'School of England Ltd\n82384592\n41 Eaton Square, London SW1W 9DH, UK', disabled: true },
      Validators.required,
    ],
    creditCard: [{ value: '**** **** **** 3456', disabled: true }, Validators.required],
  });

  slideUpAnimationInitialized = signal(false);
  lottieAnimationInitialized = signal(false);
  subscriptionPlan = this.ref.data['subscriptionPlan'];
  product = this.ref.data['product'] as WritableSignal<Product | undefined>;
  paymentData = this.ref.data['paymentData'] as WritableSignal<PaymentData | undefined>;
  loading = signal(false);
  completed = signal(false);

  customerName = computed(() => {
    const paymentData = this.paymentData();

    if (!paymentData) {
      return null;
    }

    return paymentData.customerName;
  });
  paymentMethod = computed(() => {
    const paymentData = this.paymentData();

    if (!paymentData) {
      return null;
    }

    return paymentData.paymentMethods[0];
  });
  paymentAddress = computed(() => {
    const paymentData = this.paymentData();

    if (!paymentData) {
      return null;
    }

    return paymentData.paymentAddress;
  });
  currency = computed(() => {
    const product = this.product();

    if (!product) {
      return environment.stripe.defaultCurrency;
    }

    return product.metadata['default_price_currency'];
  });
  defaultPrice = computed(() => {
    const product = this.product();

    if (!product) {
      return 0;
    }

    const defaultPrice = product.metadata['default_price'];

    return isDefined(defaultPrice) ? Number(defaultPrice) : 0;
  });
  discountPercentage = computed(() => {
    const product = this.product();

    if (!product) {
      return 0;
    }

    const discountPercentage = product.metadata['discount_percent'];

    return isDefined(discountPercentage) ? Number(discountPercentage) : 0;
  });
  discountValue = computed(() => {
    const discountPercentage = this.discountPercentage();

    if (!discountPercentage) {
      return 1;
    }

    return 1 - discountPercentage / 100;
  });

  lottieOptions: WritableSignal<AnimationOptions> = signal({
    path: '/assets/lottie/sonar-effect.json',
    loop: false,
    autoplay: true,
  });

  private animationDelay = 400;
  private lottieAnimationDelay = 500;

  ngOnInit(): void {
    const paymentCompleted = this.ref.data['completed'];
    const tokens = this.ref.data['tokens'];

    if (tokens) {
      this.amount.patchValue(Number(tokens));
    }

    if (paymentCompleted) {
      this.completed.set(true);
      this.startAnimation();
    }

    this.setPaymentMethodInformation();
  }

  updateAmount(amount: number): void {
    let newAmount = amount;

    if (amount <= 0) {
      newAmount = 1;
    }

    this.amount.patchValue(newAmount);
  }

  buy(): void {
    if (this.buyTokensForm.valid) {
      this.loading.set(true);
      this.ref.close({ amount: Number(this.amount.value), priceId: this.product()?.defaultPrice?.id });
    }
  }

  changePaymentInformation(): void {
    this.ref.close({ portal: true });
  }

  private setPaymentMethodInformation(): void {
    const paymentMethod = this.paymentMethod();
    const paymentAddress = this.paymentAddress();
    const customerName = this.customerName();

    if (paymentMethod) {
      this.creditCard.patchValue(`**** **** **** ${paymentMethod.last4}`);
    }

    if (paymentAddress) {
      this.billingAddress.patchValue(
        `${customerName || ''}\n${paymentAddress.postalCode}\n${paymentAddress.line1}, ${paymentAddress.city}, ${paymentAddress.country}`,
      );
    }
  }

  private async startAnimation(): Promise<void> {
    const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

    await delay(this.animationDelay);
    this.slideUpAnimationInitialized.set(true);

    await delay(this.lottieAnimationDelay);
    // TODO: Enable with the correct lottie file
    // this.lottieAnimationInitialized.set(true);
  }

  get billingAddress(): FormControl {
    return this.buyTokensForm.get('billingAddress') as FormControl;
  }

  get creditCard(): FormControl {
    return this.buyTokensForm.get('creditCard') as FormControl;
  }

  get amount(): FormControl {
    return this.buyTokensForm.get('amount') as FormControl;
  }
}
